import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sb } from '../../supabase';
import { buildAuthCallbackUrl } from '../../lib/authRedirects';
import { claimStart, claimVerify } from '../../lib/phoneLink';
import { saveCheckoutState } from '../../lib/checkoutState';
import { track } from '../../lib/growthTracker';
import { otpReason } from '../../lib/growthEvents';

// "Complete purchase" for a guest: verify who you are WITHOUT leaving checkout
// and without a password (owner, 2026-10-21). The cart, the delivery details
// and the calculated quote stay put (checkoutState.js); the page continues to
// payment on its own once a session exists.
//
//   WhatsApp code to the number already typed for delivery (default):
//     claim_start first asks the server how this number may be used —
//       use_signin  → GoTrue's own code: signs in its proven owner, or creates
//                     a passwordless buyer account for a new number;
//       sent        → the number sits on an account that never proved it: our
//                     reclaim code; typed back, the number is freed, then the
//                     normal code follows;
//       unavailable → this number cannot sign in by code (proven by another
//                     kind of account, or an older unproven one): use another way.
//     So a number that was never proven never signs anyone into an account.
//   Google / Apple (Apple only when REACT_APP_APPLE_WEB_AUTH=1 — it needs the
//     owner's Apple Services ID): a round trip back to /market/checkout?resume=1.
//   Email code: passwordless, in place.
// A typed number never becomes a sign-in method of a Google/Apple/email
// account here — linking a number is phone-link's job, with its own code.

const T = {
  ar: {
    title: 'أكمل الشراء', lead: 'تحقّق من رقمك لنحفظ طلبك ونكمل الدفع — بدون كلمة مرور.',
    viaWa: (n) => `أرسل رمز التحقق عبر واتساب إلى ${n}`, sending: 'جارٍ الإرسال…', sentTo: (n) => `أرسلنا رمزًا إلى واتساب ${n}.`,
    code: 'رمز التحقق', confirm: 'تأكيد والمتابعة للدفع', verifying: 'جارٍ التحقق…', resend: 'إعادة الإرسال',
    reclaimLead: 'هذا الرقم مسجّل في معبر دون توثيق. أدخل الرمز الذي أرسلناه لنثبت أنه رقمك.', reclaimConfirm: 'تأكيد الرقم',
    unavailable: 'لا يمكن استخدام هذا الرقم للدخول بالرمز حاليًا. اختر Google أو البريد الإلكتروني، أو تواصل معنا.',
    or: 'أو تابع باستخدام', google: 'المتابعة مع Google', apple: 'المتابعة مع Apple', email: 'رمز عبر البريد الإلكتروني',
    emailPh: 'بريدك الإلكتروني', sendEmail: 'أرسل الرمز', emailSent: (e) => `أرسلنا رمزًا إلى ${e}.`,
    badCode: 'الرمز غير صحيح أو انتهت صلاحيته.', rate: 'محاولات كثيرة خلال وقت قصير. انتظر قليلًا ثم أعد المحاولة.',
    failed: 'تعذّر الإرسال. حاول مرة أخرى.', badEmail: 'أدخل بريدًا إلكترونيًا صحيحًا.', cancel: 'رجوع',
    password: 'لديك حساب بكلمة مرور؟ سجّل الدخول',
  },
  en: {
    title: 'Complete your purchase', lead: 'Verify your number so we can save your order and continue to payment — no password.',
    viaWa: (n) => `Send a code via WhatsApp to ${n}`, sending: 'Sending…', sentTo: (n) => `We sent a code to WhatsApp ${n}.`,
    code: 'Verification code', confirm: 'Confirm and continue to payment', verifying: 'Verifying…', resend: 'Resend',
    reclaimLead: 'This number is registered on Maabar without verification. Enter the code we sent to prove it is yours.', reclaimConfirm: 'Confirm the number',
    unavailable: "This number can't be used to sign in by code right now. Use Google or email, or contact us.",
    or: 'Or continue with', google: 'Continue with Google', apple: 'Continue with Apple', email: 'Email code',
    emailPh: 'Your email', sendEmail: 'Send code', emailSent: (e) => `We sent a code to ${e}.`,
    badCode: 'The code is wrong or has expired.', rate: 'Too many attempts in a short time. Please wait a moment.',
    failed: "We couldn't send it. Please try again.", badEmail: 'Enter a valid email address.', cancel: 'Back',
    password: 'Have an account with a password? Sign in',
  },
  zh: {
    title: '完成购买', lead: '验证您的号码，即可保存订单并继续付款——无需密码。',
    viaWa: (n) => `通过 WhatsApp 发送验证码至 ${n}`, sending: '发送中…', sentTo: (n) => `验证码已发送至 WhatsApp ${n}。`,
    code: '验证码', confirm: '确认并继续付款', verifying: '验证中…', resend: '重新发送',
    reclaimLead: '此号码已在 Maabar 注册但未经验证。请输入我们发送的验证码，证明这是您的号码。', reclaimConfirm: '确认号码',
    unavailable: '此号码暂时无法用验证码登录。请使用 Google 或邮箱，或联系我们。',
    or: '或使用以下方式继续', google: '使用 Google 继续', apple: '使用 Apple 继续', email: '邮箱验证码',
    emailPh: '您的邮箱', sendEmail: '发送验证码', emailSent: (e) => `验证码已发送至 ${e}。`,
    badCode: '验证码错误或已过期。', rate: '尝试次数过多，请稍后再试。',
    failed: '发送失败，请重试。', badEmail: '请输入有效的邮箱地址。', cancel: '返回',
    password: '已有密码账户？登录',
  },
};

const isolate = (n) => `⁨${n}⁩`;
const APPLE_WEB = process.env.REACT_APP_APPLE_WEB_AUTH === '1';
const rateLike = (e) => e?.status === 429 || /rate|too many|security purposes/i.test(String(e?.message || ''));

export default function CheckoutAuth({ delivery, lang = 'ar', font, onCancel }) {
  const t = T[lang] || T.ar;
  const nav = useNavigate();
  const phone = delivery?.phone || '';
  const meta = { full_name: delivery?.name || '', role: 'buyer', lang };
  const [step, setStep] = useState('start');   // start | reclaim | code | email | emailCode
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  const sendGoTrueCode = async () => {
    const { error } = await sb.auth.signInWithOtp({ phone, options: { shouldCreateUser: true, data: meta } });
    if (error) {
      console.error('[CheckoutAuth] signInWithOtp(phone):', error.status || '', error.message || error);
      // The ownership gate refusing an unproven number reads as "unavailable", not as a fault.
      setErr(rateLike(error) ? t.rate : /proven|403|forbidden/i.test(String(error.message)) ? t.unavailable : t.failed);
      track('otp_failed', { channel: 'whatsapp', mode: 'checkout', reason: otpReason(error) });
      return false;
    }
    track('otp_requested', { channel: 'whatsapp', mode: 'checkout' });
    setStep('code'); setCode(''); setInfo(t.sentTo(isolate(phone)));
    return true;
  };

  const startPhone = async () => {
    track('auth_method_chosen', { method: 'phone', mode: 'checkout' });
    setBusy('send'); setErr(''); setInfo('');
    const r = await claimStart(phone, lang);
    if (r.ok && r.sent) { setStep('reclaim'); setCode(''); setInfo(t.reclaimLead); setBusy(''); return; }
    if (r.error === 'use_signin') { await sendGoTrueCode(); setBusy(''); return; }
    setBusy('');
    setErr(r.error === 'unavailable' ? t.unavailable : r.error === 'rate_limited' ? t.rate : t.failed);
  };

  const confirmReclaim = async () => {
    setBusy('verify'); setErr('');
    const r = await claimVerify(phone, code.replace(/\D/g, ''));
    if (!r.ok) { setBusy(''); setErr(r.error === 'unavailable' ? t.unavailable : t.badCode); return; }
    await sendGoTrueCode();
    setBusy('');
  };

  const confirmPhone = async () => {
    setBusy('verify'); setErr('');
    const { error } = await sb.auth.verifyOtp({ phone, token: code.replace(/\D/g, ''), type: 'sms' });
    setBusy('');
    if (error) { console.warn('[CheckoutAuth] verifyOtp(sms):', error.status || '', error.message || error); setErr(t.badCode); track('otp_failed', { channel: 'whatsapp', mode: 'checkout', reason: otpReason(error) }); }
    else track('otp_verified', { channel: 'whatsapp', mode: 'checkout' });
    // On success the app's auth listener delivers the session; the page continues to payment.
  };

  const oauth = async (provider) => {
    track('auth_method_chosen', { method: provider, mode: 'checkout' });
    setErr('');
    saveCheckoutState({ resume: true, resumeAt: Date.now() });
    const { error } = await sb.auth.signInWithOAuth({ provider, options: { redirectTo: buildAuthCallbackUrl('/market/checkout?resume=1', 'buyer') } });
    if (error) { console.error(`[CheckoutAuth] ${provider}:`, error.message || error); setErr(t.failed); }
  };

  const sendEmail = async () => {
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setErr(t.badEmail); return; }
    track('auth_method_chosen', { method: 'email', mode: 'checkout' });
    setBusy('send'); setErr('');
    const { error } = await sb.auth.signInWithOtp({ email: e, options: { shouldCreateUser: true, data: meta } });
    setBusy('');
    if (error) { console.error('[CheckoutAuth] signInWithOtp(email):', error.status || '', error.message || error); setErr(rateLike(error) ? t.rate : t.failed); track('otp_failed', { channel: 'email', mode: 'checkout', reason: otpReason(error) }); return; }
    track('otp_requested', { channel: 'email', mode: 'checkout' });
    setStep('emailCode'); setCode(''); setInfo(t.emailSent(e));
  };
  const confirmEmail = async () => {
    setBusy('verify'); setErr('');
    const token = code.replace(/\D/g, '');
    let { error } = await sb.auth.verifyOtp({ email: email.trim().toLowerCase(), token, type: 'email' });
    if (error) ({ error } = await sb.auth.verifyOtp({ email: email.trim().toLowerCase(), token, type: 'signup' }));
    setBusy('');
    if (error) { console.warn('[CheckoutAuth] verifyOtp(email):', error.status || '', error.message || error); setErr(t.badCode); track('otp_failed', { channel: 'email', mode: 'checkout', reason: otpReason(error) }); }
    else track('otp_verified', { channel: 'email', mode: 'checkout' });
  };

  const box = { marginTop: 12, padding: 14, borderRadius: 14, border: '1px solid var(--border)', background: 'var(--surface)', fontFamily: font };
  const btn = (on, ghost = false) => ({ width: '100%', padding: '11px 0', borderRadius: 12, fontSize: 13.5, fontWeight: 600, fontFamily: font, cursor: on ? 'pointer' : 'not-allowed',
    border: ghost ? '1px solid var(--border)' : 'none', background: ghost ? 'var(--surface-raised)' : on ? '#1A1814' : 'var(--border)', color: ghost ? 'var(--text-primary)' : on ? '#fff' : 'var(--text-muted)' });
  const codeInput = (
    <input data-checkout-auth-code="1" inputMode="numeric" autoComplete="one-time-code" dir="ltr" maxLength={10} value={code}
      onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 10)); setErr(''); }} placeholder={t.code} aria-label={t.code}
      style={{ width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--border-strong, var(--border))', fontSize: 16, letterSpacing: 3, textAlign: 'center', marginBottom: 8 }} />
  );

  return (
    <div data-checkout-auth={step} data-clarity-mask="True" style={box}>
      <strong style={{ fontSize: 14 }}>{t.title}</strong>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '4px 0 10px', lineHeight: 1.6 }}>{t.lead}</p>

      {step === 'start' && (
        <button type="button" data-auth-wa="1" disabled={!phone || !!busy} onClick={startPhone} style={btn(!!phone && !busy)}>
          {busy === 'send' ? t.sending : t.viaWa(isolate(phone))}
        </button>
      )}
      {(step === 'reclaim' || step === 'code') && (
        <>
          {info && <p data-auth-info="1" style={{ fontSize: 12.5, margin: '0 0 8px', lineHeight: 1.6 }}>{info}</p>}
          {codeInput}
          <button type="button" data-auth-confirm={step} disabled={code.length < 4 || !!busy} onClick={step === 'reclaim' ? confirmReclaim : confirmPhone} style={btn(code.length >= 4 && !busy)}>
            {busy === 'verify' ? t.verifying : step === 'reclaim' ? t.reclaimConfirm : t.confirm}
          </button>
          <button type="button" onClick={startPhone} disabled={!!busy} style={{ background: 'none', border: 'none', color: '#9A7636', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, marginTop: 6, fontFamily: font }}>{t.resend}</button>
        </>
      )}
      {step === 'email' && (
        <>
          <input data-auth-email="1" type="email" autoComplete="email" dir="ltr" value={email} onChange={(e) => { setEmail(e.target.value); setErr(''); }} placeholder={t.emailPh}
            style={{ width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--border-strong, var(--border))', fontSize: 14, marginBottom: 8 }} />
          <button type="button" data-auth-email-send="1" disabled={!!busy} onClick={sendEmail} style={btn(!busy)}>{busy === 'send' ? t.sending : t.sendEmail}</button>
        </>
      )}
      {step === 'emailCode' && (
        <>
          {info && <p style={{ fontSize: 12.5, margin: '0 0 8px' }}>{info}</p>}
          {codeInput}
          <button type="button" data-auth-confirm="email" disabled={code.length < 4 || !!busy} onClick={confirmEmail} style={btn(code.length >= 4 && !busy)}>{busy === 'verify' ? t.verifying : t.confirm}</button>
        </>
      )}
      {err && <p data-auth-error="1" role="alert" style={{ color: '#B3261E', fontSize: 12.5, margin: '8px 0 0', lineHeight: 1.6 }}>{err}</p>}

      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '12px 0 6px', textAlign: 'center' }}>{t.or}</p>
      <div style={{ display: 'grid', gap: 6 }}>
        <button type="button" data-auth-google="1" onClick={() => oauth('google')} style={btn(true, true)}>{t.google}</button>
        {APPLE_WEB && <button type="button" data-auth-apple="1" onClick={() => oauth('apple')} style={btn(true, true)}>{t.apple}</button>}
        {step !== 'email' && step !== 'emailCode' && <button type="button" data-auth-email-start="1" onClick={() => { setStep('email'); setErr(''); setInfo(''); }} style={btn(true, true)}>{t.email}</button>}
      </div>
      {/* Existing accounts that sign in with a password (incl. older ones whose
          number cannot sign in by code): the usual sign-in page, then back here —
          the 30-minute resume signal carries on to payment. */}
      <button type="button" data-auth-password="1" onClick={() => { saveCheckoutState({ resume: true, resumeAt: Date.now() }); nav(`/login?next=${encodeURIComponent('/market/checkout')}`); }}
        style={{ background: 'none', border: 'none', color: '#9A7636', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, marginTop: 10, fontFamily: font, display: 'block' }}>{t.password}</button>
      {onCancel && <button type="button" onClick={onCancel} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, marginTop: 8, fontFamily: font }}>{t.cancel}</button>}
    </div>
  );
}
